import { useState } from "react";
import { useData } from "../context/DataContext";
import { Trash2, Plus, Edit } from "lucide-react";
import { Button, EmptyState, Modal, Input } from "./ui";
import { useDeleteConfirmation, useFormReset } from "../utils/hooks";

const Faecher = () => {
  const { data, addFach, updateFach, deleteFach, navigateToFachDetail } = useData();

  // Get unique profiles from klassen
  const uniqueProfiles = Array.from(
    new Set(data.klassen.map((k) => k.profil))
  ).sort();

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { formData, setFormData, resetForm } = useFormReset({
    kuerzel: "",
    name: "",
    basislektionen: "",
    basislektionenAltersentlastung: "",
    pflichtfach: { 1: false, 2: false, 3: false, 4: false, 5: false, 6: false },
    freifach: { 1: false, 2: false, 3: false, 4: false, 5: false, 6: false },
    hk: { 1: false, 2: false, 3: false, 4: false, 5: false, 6: false },
    profileConfig: {} as { [key: string]: any },
  });

  const [selectedProfile, setSelectedProfile] = useState<string>("");
  const [showProfileConfig, setShowProfileConfig] = useState(false);

  const confirmDelete = useDeleteConfirmation("Fach", deleteFach);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.kuerzel || !formData.name) {
      alert("Bitte Kürzel und Name eingeben!");
      return;
    }

    const fachData = {
      kuerzel: formData.kuerzel,
      name: formData.name,
      basislektionen: formData.basislektionen ? parseFloat(formData.basislektionen) : undefined,
      basislektionenAltersentlastung: formData.basislektionenAltersentlastung ? parseFloat(formData.basislektionenAltersentlastung) : undefined,
      pflichtfach: formData.pflichtfach,
      freifach: formData.freifach,
      hk: formData.hk,
      profileConfig: Object.keys(formData.profileConfig).length > 0
        ? formData.profileConfig
        : undefined,
    };

    if (editingId) {
      updateFach(editingId, fachData);
    } else {
      addFach(fachData);
    }
    resetForm();
    setEditingId(null);
    setShowModal(false);
    setSelectedProfile("");
    setShowProfileConfig(false);
  };

  const handleEdit = (fachId: string) => {
    const fach = data.faecher.find((f) => f.id === fachId);
    if (fach) {
      setFormData({
        kuerzel: fach.kuerzel,
        name: fach.name,
        basislektionen: fach.basislektionen?.toString() ?? "",
        basislektionenAltersentlastung: fach.basislektionenAltersentlastung?.toString() ?? "",
        pflichtfach: { ...fach.pflichtfach },
        freifach: { ...fach.freifach },
        hk: { ...fach.hk },
        profileConfig: fach.profileConfig ? { ...fach.profileConfig } : {},
      });
      setEditingId(fachId);
      setShowModal(true);
    }
  };

  const handleNewFach = () => {
    resetForm();
    setEditingId(null);
    setShowModal(true);
  };

  const togglePflichtfach = (stufe: 1 | 2 | 3 | 4 | 5 | 6) => {
    const newPflichtfach = !formData.pflichtfach[stufe];
    setFormData({
      ...formData,
      pflichtfach: {
        ...formData.pflichtfach,
        [stufe]: newPflichtfach,
      },
      // If enabling pflichtfach, disable freifach (mutually exclusive)
      freifach: {
        ...formData.freifach,
        [stufe]: newPflichtfach ? false : formData.freifach[stufe],
      },
      // If unchecking pflichtfach and freifach is also unchecked, disable HK
      hk: {
        ...formData.hk,
        [stufe]: newPflichtfach || formData.freifach[stufe] ? formData.hk[stufe] : false,
      },
    });
  };

  const toggleFreifach = (stufe: 1 | 2 | 3 | 4 | 5 | 6) => {
    const newFreifach = !formData.freifach[stufe];
    setFormData({
      ...formData,
      freifach: {
        ...formData.freifach,
        [stufe]: newFreifach,
      },
      // If enabling freifach, disable pflichtfach (mutually exclusive)
      pflichtfach: {
        ...formData.pflichtfach,
        [stufe]: newFreifach ? false : formData.pflichtfach[stufe],
      },
      // If unchecking freifach and pflichtfach is also unchecked, disable HK
      hk: {
        ...formData.hk,
        [stufe]: newFreifach || formData.pflichtfach[stufe] ? formData.hk[stufe] : false,
      },
    });
  };

  const toggleHk = (stufe: 1 | 2 | 3 | 4 | 5 | 6) => {
    setFormData({
      ...formData,
      hk: {
        ...formData.hk,
        [stufe]: !formData.hk[stufe],
      },
    });
  };

  // Profile-specific toggle functions
  const toggleProfilePflichtfach = (profile: string, stufe: 1 | 2 | 3 | 4 | 5 | 6) => {
    const currentProfile = formData.profileConfig[profile] || {
      pflichtfach: { 1: false, 2: false, 3: false, 4: false, 5: false, 6: false },
      freifach: { 1: false, 2: false, 3: false, 4: false, 5: false, 6: false },
      hk: { 1: false, 2: false, 3: false, 4: false, 5: false, 6: false },
    };

    const newPflichtfach = !currentProfile.pflichtfach[stufe];

    setFormData({
      ...formData,
      profileConfig: {
        ...formData.profileConfig,
        [profile]: {
          ...currentProfile,
          pflichtfach: {
            ...currentProfile.pflichtfach,
            [stufe]: newPflichtfach,
          },
          freifach: {
            ...currentProfile.freifach,
            [stufe]: newPflichtfach ? false : currentProfile.freifach[stufe],
          },
          hk: {
            ...currentProfile.hk,
            [stufe]: newPflichtfach || currentProfile.freifach[stufe] ? currentProfile.hk[stufe] : false,
          },
        },
      },
    });
  };

  const toggleProfileFreifach = (profile: string, stufe: 1 | 2 | 3 | 4 | 5 | 6) => {
    const currentProfile = formData.profileConfig[profile] || {
      pflichtfach: { 1: false, 2: false, 3: false, 4: false, 5: false, 6: false },
      freifach: { 1: false, 2: false, 3: false, 4: false, 5: false, 6: false },
      hk: { 1: false, 2: false, 3: false, 4: false, 5: false, 6: false },
    };

    const newFreifach = !currentProfile.freifach[stufe];

    setFormData({
      ...formData,
      profileConfig: {
        ...formData.profileConfig,
        [profile]: {
          ...currentProfile,
          freifach: {
            ...currentProfile.freifach,
            [stufe]: newFreifach,
          },
          pflichtfach: {
            ...currentProfile.pflichtfach,
            [stufe]: newFreifach ? false : currentProfile.pflichtfach[stufe],
          },
          hk: {
            ...currentProfile.hk,
            [stufe]: newFreifach || currentProfile.pflichtfach[stufe] ? currentProfile.hk[stufe] : false,
          },
        },
      },
    });
  };

  const toggleProfileHk = (profile: string, stufe: 1 | 2 | 3 | 4 | 5 | 6) => {
    const currentProfile = formData.profileConfig[profile] || {
      pflichtfach: { 1: false, 2: false, 3: false, 4: false, 5: false, 6: false },
      freifach: { 1: false, 2: false, 3: false, 4: false, 5: false, 6: false },
      hk: { 1: false, 2: false, 3: false, 4: false, 5: false, 6: false },
    };

    setFormData({
      ...formData,
      profileConfig: {
        ...formData.profileConfig,
        [profile]: {
          ...currentProfile,
          hk: {
            ...currentProfile.hk,
            [stufe]: !currentProfile.hk[stufe],
          },
        },
      },
    });
  };

  const handleAddProfile = () => {
    if (!selectedProfile || selectedProfile.trim() === "") {
      alert("Bitte Profil eingeben!");
      return;
    }

    if (formData.profileConfig[selectedProfile]) {
      alert("Profil bereits konfiguriert!");
      return;
    }

    setFormData({
      ...formData,
      profileConfig: {
        ...formData.profileConfig,
        [selectedProfile]: {
          pflichtfach: { 1: false, 2: false, 3: false, 4: false, 5: false, 6: false },
          freifach: { 1: false, 2: false, 3: false, 4: false, 5: false, 6: false },
          hk: { 1: false, 2: false, 3: false, 4: false, 5: false, 6: false },
        },
      },
    });
    setSelectedProfile("");
  };

  const handleRemoveProfile = (profile: string) => {
    const newProfileConfig = { ...formData.profileConfig };
    delete newProfileConfig[profile];
    setFormData({
      ...formData,
      profileConfig: newProfileConfig,
    });
  };

  return (
    <div className="space-y-6">
      {/* Action Bar */}
      <div className="flex flex-wrap gap-3">
        <Button
          onClick={handleNewFach}
          variant="primary"
          icon={<Plus className="w-4 h-4" />}
        >
          Neues Fach
        </Button>
      </div>

      {/* Main Content */}
      {data.faecher.length === 0 ? (
        <EmptyState
          icon="📖"
          title="Keine Fächer vorhanden."
          description='Klicken Sie auf "Neues Fach" oder laden Sie Demo-Daten über die Sidebar'
        />
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kürzel
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Basis
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stufe 1
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stufe 2
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stufe 3
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stufe 4
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stufe 5
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stufe 6
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.faecher.map((fach) => {
                  const renderStufeCell = (stufe: 1 | 2 | 3 | 4 | 5 | 6) => {
                    const badges = [];
                    if (fach.pflichtfach?.[stufe]) {
                      badges.push(
                        <span key="p" className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" title="Pflichtfach">
                          P
                        </span>
                      );
                    }
                    if (fach.freifach?.[stufe]) {
                      badges.push(
                        <span key="f" className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" title="Freifach">
                          F
                        </span>
                      );
                    }
                    if (fach.hk?.[stufe]) {
                      badges.push(
                        <span key="h" className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800" title="Halbklasse">
                          H
                        </span>
                      );
                    }
                    return badges.length > 0 ? (
                      <div className="flex flex-wrap gap-1 justify-center">{badges}</div>
                    ) : (
                      <span className="text-gray-300">-</span>
                    );
                  };

                  return (
                    <tr
                      key={fach.id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => navigateToFachDetail(fach.id)}
                    >
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="font-medium text-gray-900 text-primary-600 hover:text-primary-800">
                          {fach.kuerzel}
                        </div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {fach.name}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center text-sm text-gray-900">
                        {fach.basislektionen ? (
                          <span>
                            {fach.basislektionen}
                            {fach.basislektionenAltersentlastung && (
                              <span className="text-gray-400"> / {fach.basislektionenAltersentlastung}</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center">
                        {renderStufeCell(1)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center">
                        {renderStufeCell(2)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center">
                        {renderStufeCell(3)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center">
                        {renderStufeCell(4)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center">
                        {renderStufeCell(5)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center">
                        {renderStufeCell(6)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleEdit(fach.id)}
                            className="inline-flex items-center p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Bearbeiten"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => confirmDelete(fach.id, fach.name)}
                            className="inline-flex items-center p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Löschen"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingId ? "Fach bearbeiten" : "Neues Fach"}
      >
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Input
            label="Kürzel *"
            type="text"
            required
            value={formData.kuerzel}
            onChange={(e) =>
              setFormData({ ...formData, kuerzel: e.target.value })
            }
            placeholder="z.B. BG, Mu, D"
          />
          <Input
            label="Name *"
            type="text"
            required
            value={formData.name}
            onChange={(e) =>
              setFormData({ ...formData, name: e.target.value })
            }
            placeholder="z.B. Bildnerisches Gestalten"
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Basislektionen (100%)"
              type="number"
              min="1"
              step="1"
              value={formData.basislektionen}
              onChange={(e) =>
                setFormData({ ...formData, basislektionen: e.target.value })
              }
              placeholder="z.B. 26"
            />
            <Input
              label="Basislektionen AE"
              type="number"
              min="1"
              step="1"
              value={formData.basislektionenAltersentlastung}
              onChange={(e) =>
                setFormData({ ...formData, basislektionenAltersentlastung: e.target.value })
              }
              placeholder="z.B. 24"
            />
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Stufen-Konfiguration
              </label>
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">
                        Stufe
                      </th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-700">
                        Pflichtfach
                      </th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-700">
                        Freifach
                      </th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-700">
                        HK
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {([1, 2, 3, 4, 5, 6] as const).map((stufe) => {
                      const canHaveHk = formData.pflichtfach[stufe] || formData.freifach[stufe];
                      return (
                        <tr key={stufe} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-sm font-medium text-gray-900">
                            Stufe {stufe}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={formData.pflichtfach[stufe]}
                              onChange={() => togglePflichtfach(stufe)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                          </td>
                          <td className="px-3 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={formData.freifach[stufe]}
                              onChange={() => toggleFreifach(stufe)}
                              className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                            />
                          </td>
                          <td className="px-3 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={formData.hk[stufe]}
                              onChange={() => toggleHk(stufe)}
                              disabled={!canHaveHk}
                              className={`w-4 h-4 border-gray-300 rounded ${
                                canHaveHk
                                  ? "text-purple-600 focus:ring-purple-500 cursor-pointer"
                                  : "text-gray-300 cursor-not-allowed opacity-50"
                              }`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Ein Fach kann entweder als <strong>Pflichtfach</strong> oder <strong>Freifach</strong> markiert werden, aber nicht beides gleichzeitig. HK (Halbklasse) kann nur aktiviert werden, wenn das Fach als Pflichtfach oder Freifach markiert ist.
              </p>
            </div>

            {/* Profile-Specific Configuration */}
            <div className="space-y-2 border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">
                  Profil-spezifische Konfiguration (optional)
                </label>
                <button
                  type="button"
                  onClick={() => setShowProfileConfig(!showProfileConfig)}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  {showProfileConfig ? "Ausblenden" : "Anzeigen"}
                </button>
              </div>

              {showProfileConfig && (
                <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-600">
                    Definieren Sie abweichende Einstellungen für spezifische Profile. Die Profil-Konfiguration überschreibt die Standard-Stufen-Konfiguration.
                  </p>

                  {/* Add new profile */}
                  <div className="flex gap-2">
                    <select
                      value={selectedProfile}
                      onChange={(e) => setSelectedProfile(e.target.value)}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">- Profil wählen -</option>
                      {uniqueProfiles.map((profile) => (
                        <option key={profile} value={profile}>
                          {profile}
                        </option>
                      ))}
                    </select>
                    <Button
                      type="button"
                      onClick={handleAddProfile}
                      variant="secondary"
                      className="text-sm"
                    >
                      Hinzufügen
                    </Button>
                  </div>

                  {/* Display configured profiles */}
                  {Object.keys(formData.profileConfig).length > 0 && (
                    <div className="space-y-4">
                      {Object.entries(formData.profileConfig).map(([profile, config]) => (
                        <div key={profile} className="bg-white p-3 rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-semibold text-gray-900">
                              Profil: {profile}
                            </h4>
                            <button
                              type="button"
                              onClick={() => handleRemoveProfile(profile)}
                              className="text-xs text-red-600 hover:text-red-700 font-medium"
                            >
                              Entfernen
                            </button>
                          </div>

                          <div className="overflow-x-auto">
                            <table className="min-w-full border border-gray-200 rounded">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-700">
                                    Stufe
                                  </th>
                                  <th className="px-2 py-1 text-center text-xs font-medium text-gray-700">
                                    Pflichtfach
                                  </th>
                                  <th className="px-2 py-1 text-center text-xs font-medium text-gray-700">
                                    Freifach
                                  </th>
                                  <th className="px-2 py-1 text-center text-xs font-medium text-gray-700">
                                    HK
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {([1, 2, 3, 4, 5, 6] as const).map((stufe) => {
                                  const canHaveHk = config.pflichtfach[stufe] || config.freifach[stufe];
                                  return (
                                    <tr key={stufe} className="hover:bg-gray-50">
                                      <td className="px-2 py-1 text-xs font-medium text-gray-900">
                                        Stufe {stufe}
                                      </td>
                                      <td className="px-2 py-1 text-center">
                                        <input
                                          type="checkbox"
                                          checked={config.pflichtfach[stufe]}
                                          onChange={() => toggleProfilePflichtfach(profile, stufe)}
                                          className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                      </td>
                                      <td className="px-2 py-1 text-center">
                                        <input
                                          type="checkbox"
                                          checked={config.freifach[stufe]}
                                          onChange={() => toggleProfileFreifach(profile, stufe)}
                                          className="w-3.5 h-3.5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                        />
                                      </td>
                                      <td className="px-2 py-1 text-center">
                                        <input
                                          type="checkbox"
                                          checked={config.hk[stufe]}
                                          onChange={() => toggleProfileHk(profile, stufe)}
                                          disabled={!canHaveHk}
                                          className={`w-3.5 h-3.5 border-gray-300 rounded ${
                                            canHaveHk
                                              ? "text-purple-600 focus:ring-purple-500 cursor-pointer"
                                              : "text-gray-300 cursor-not-allowed opacity-50"
                                          }`}
                                        />
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button type="submit" variant="primary" className="flex-1">
              Speichern
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowModal(false)}
              className="flex-1"
            >
              Abbrechen
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Faecher;
